import React, { useEffect, useState } from 'react';
import { getAuditLog } from '../../services/adminService';
import AdminNav from './AdminNav';

interface AuditLogItem {
  id: string;
  adminEmail: string;
  actionType: string;
  targetUserEmail: string;
  targetResource: string | null;
  details: any;
  createdAt: string;
}

const AuditLog: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [items, setItems] = useState<AuditLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAuditLog({ page, limit });
      setItems(data.auditLog || []);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to load audit log', err);
      setError('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-gray-600">Track admin actions and changes</p>
          <AdminNav />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-gray-500">Loading...</div>
        ) : (
          <div className="bg-white rounded shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Recent Actions</h2>
            </div>
            <div className="p-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map(item => (
                    <tr key={item.id}>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{new Date(item.createdAt).toLocaleString()}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{item.adminEmail}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{item.actionType}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{item.targetUserEmail}</td>
                      <td className="px-3 py-2 text-sm text-gray-700 max-w-md truncate" title={JSON.stringify(item.details)}>
                        <code className="text-gray-600">{typeof item.details === 'object' ? JSON.stringify(item.details) : String(item.details)}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">Total: {total}</div>
                <div className="space-x-2">
                  <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="px-3 py-1 bg-white border border-gray-300 rounded disabled:opacity-50">Prev</button>
                  <span className="text-sm text-gray-700">Page {page} of {totalPages}</span>
                  <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="px-3 py-1 bg-white border border-gray-300 rounded disabled:opacity-50">Next</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLog;

